import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useRef } from 'preact/hooks';
import { Search, Loader2, X } from 'lucide-preact';
import { BRAND_CLASSES } from '@/constants/theme';
/**
 * TypeAheadDropdown - A searchable dropdown component
 *
 * Features:
 * - Controlled search input (caller is responsible for debouncing via useDebouncedValue)
 * - Minimum character requirement before search
 * - Loading state during search
 * - Custom option rendering
 * - Click outside to close
 * - Keyboard navigation (arrow keys, enter, escape)
 *
 * @example
 * ```tsx
 * const [searchValue, setSearchValue] = useState('')
 * const [loading, setLoading] = useState(false)
 * const [results, setResults] = useState<User[]>([])
 *
 * // Fetch results when search value changes (after debounce)
 * useEffect(() => {
 *   if (searchValue.length >= 3) {
 *     setLoading(true)
 *     api.searchUsers(searchValue).then(data => {
 *       setResults(data)
 *       setLoading(false)
 *     })
 *   }
 * }, [searchValue])
 *
 * <TypeAheadDropdown
 *   label="Search Users"
 *   placeholder="Type to search..."
 *   searchValue={searchValue}
 *   onSearchChange={setSearchValue}
 *   loading={loading}
 *   options={results.map(user => ({
 *     key: user.id,
 *     label: `${user.firstName} ${user.lastName}`,
 *     data: user,
 *     render: () => (
 *       <div className="flex items-center gap-2">
 *         <img src={user.avatar} className="w-6 h-6 rounded-full" />
 *         <span>{user.firstName} {user.lastName}</span>
 *       </div>
 *     )
 *   }))}
 *   onSelect={(option) => console.log('Selected:', option.data)}
 *   onClear={() => setSearchValue('')}
 * />
 * ```
 */
export function TypeAheadDropdown({ label, required = false, name, placeholder = 'Type to search...', error, helperText, minSearchLength = 3, disabled = false, containerClassName = '', inputClassName = '', searchValue, onSearchChange, loading = false, options, onSelect, onClear, showClearButton = true, noResultsMessage = 'No results found', minLengthMessage }) {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const hasError = !!error;
    const rightPadding = showClearButton && searchValue ? 'pr-10' : 'pr-3';
    const inputClasses = `w-full pl-10 ${rightPadding} py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${hasError
        ? `${BRAND_CLASSES.INPUT.ERROR}`
        : `${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${inputClassName}`;
    const showMinLengthMessage = searchValue.length > 0 && searchValue.length < minSearchLength;
    const showDropdown = isOpen && (loading || options.length > 0 || showMinLengthMessage || (searchValue.length >= minSearchLength && options.length === 0));
    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Open dropdown when search value changes and meets minimum length
    useEffect(() => {
        if (searchValue.length >= minSearchLength || showMinLengthMessage) {
            setIsOpen(true);
        }
        else {
            setIsOpen(false);
        }
    }, [searchValue, minSearchLength, showMinLengthMessage]);
    // Reset focused index when options change
    useEffect(() => {
        setFocusedIndex(-1);
    }, [options]);
    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen)
            return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => (prev < options.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < options.length) {
                    handleSelect(options[focusedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setFocusedIndex(-1);
                inputRef.current?.blur();
                break;
        }
    };
    const handleSelect = (option) => {
        onSelect(option);
        setIsOpen(false);
        setFocusedIndex(-1);
        // Don't clear search value - let parent component decide
    };
    const handleClearClick = () => {
        onSearchChange('');
        if (onClear)
            onClear();
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.focus();
    };
    return (_jsxs("div", { className: containerClassName, ref: containerRef, children: [label && (_jsxs("label", { htmlFor: name, className: `block text-sm font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-2`, children: [label, required && _jsx("span", { className: `${BRAND_CLASSES.INPUT.REQUIRED} ml-1`, children: "*" })] })), _jsxs("div", { className: "relative", children: [_jsx("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none", children: _jsx(Search, { className: "h-4 w-4 text-slate-400 dark:text-slate-500" }) }), _jsx("input", { ref: inputRef, type: "text", id: name, name: name, className: inputClasses, placeholder: placeholder, value: searchValue, onChange: (e) => onSearchChange(e.target.value), onKeyDown: handleKeyDown, onFocus: () => {
                            if (searchValue.length >= minSearchLength || showMinLengthMessage) {
                                setIsOpen(true);
                            }
                        }, disabled: disabled, autoComplete: "off" }), showClearButton && searchValue && !disabled && (_jsx("button", { type: "button", onClick: handleClearClick, className: "absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors", "aria-label": "Clear search", children: _jsx(X, { className: "h-4 w-4" }) })), showDropdown && (_jsx("div", { className: "absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-auto", children: loading ? (_jsxs("div", { className: "flex items-center justify-center gap-2 p-4 text-sm text-slate-600 dark:text-slate-400", children: [_jsx(Loader2, { className: "h-4 w-4 animate-spin" }), _jsx("span", { children: "Searching..." })] })) : showMinLengthMessage ? (_jsx("div", { className: "p-4 text-sm text-slate-500 dark:text-slate-400", children: minLengthMessage || `Type at least ${minSearchLength} characters to search` })) : options.length === 0 ? (_jsx("div", { className: "p-4 text-sm text-slate-500 dark:text-slate-400", children: noResultsMessage })) : (_jsx("ul", { className: "py-1", children: options.map((option, index) => (_jsx("li", { children: _jsx("button", { type: "button", onClick: () => handleSelect(option), className: `w-full text-left px-4 py-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${index === focusedIndex ? 'bg-slate-100 dark:bg-slate-700' : ''}`, children: option.render ? option.render() : (_jsx("span", { className: "text-sm text-slate-900 dark:text-white", children: option.label })) }) }, option.key))) })) }))] }), error && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error })), helperText && !error && (_jsx("p", { className: `mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`, children: helperText }))] }));
}
